import React, { createContext, useContext, useReducer, useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/api';

const CartContext = createContext(null);

function getSessionId() {
  let id = localStorage.getItem('gw_session_id');
  if (!id) { id = 'sess_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8); localStorage.setItem('gw_session_id', id); }
  return id;
}

function getStoredUser() {
  try { return JSON.parse(localStorage.getItem('gw_user')) || null; } catch { return null; }
}

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const cartKey = action.product._id + (action.product.selectedColor?.name || '');
      const existing = state.find(i => (i._id + (i.selectedColor?.name || '')) === cartKey);
      if (existing) {
        return state.map(i =>
          (i._id + (i.selectedColor?.name || '')) === cartKey
            ? { ...i, qty: i.qty + action.qty }
            : i
        );
      }
      return [...state, { ...action.product, qty: action.qty }];
    }
    case 'REMOVE':
      return state.filter(i => (i._id + (i.selectedColor?.name || '')) !== action.id);
    case 'UPDATE_QTY':
      return state.map(i =>
        (i._id + (i.selectedColor?.name || '')) === action.id ? { ...i, qty: Math.max(1, action.qty) } : i
      );
    case 'SYNC':
      return action.cart;
    case 'CLEAR':
      return [];
    default:
      return state;
  }
}

function loadCart() {
  try {
    const saved = localStorage.getItem('gw_cart');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [cart, dispatch] = useReducer(cartReducer, [], loadCart);
  const [syncNotes, setSyncNotes] = useState([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const sessionId = useRef(getSessionId());
  const userRef = useRef(null);
  if (userRef.current === null) {
    userRef.current = getStoredUser();
  }

  // Save cart to CartProcess DB on every change
  const saveCartProcess = useCallback(async (currentCart, user, status) => {
    try {
      var resolvedUser = (user !== undefined) ? user : userRef.current;
      if (!resolvedUser) { resolvedUser = getStoredUser(); }
      var userId = '';
      if (resolvedUser && resolvedUser._id) { userId = String(resolvedUser._id); }
      var subtotal = currentCart.reduce(function(s, i) { return s + i.price * i.qty; }, 0);
      await api.post('/cart-process', {
        sessionId: sessionId.current,
        userId: userId,
        customer: resolvedUser ? { name: resolvedUser.name, mobile: resolvedUser.mobile, email: resolvedUser.email, address: resolvedUser.address } : {},
        items: currentCart.map(function(i) { return { _id: i._id, name: i.name, price: i.price, qty: i.qty, image: (i.images && i.images[0]) || i.image || '', category: i.category, selectedColor: i.selectedColor || null }; }),
        subtotal: subtotal,
        status: status || 'active',
      });
    } catch(e) {}
  }, []);

  const openCartDrawer = () => setCartDrawerOpen(true);
  const closeCartDrawer = () => setCartDrawerOpen(false);

  // Keep a ref so syncCart always reads the latest cart without stale closure
  const cartRef = useRef(cart);
  useEffect(() => { cartRef.current = cart; }, [cart]);

  useEffect(() => {
    localStorage.setItem('gw_cart', JSON.stringify(cart));
    const t = setTimeout(() => saveCartProcess(cart, undefined, 'active'), 800);
    return () => clearTimeout(t);
  }, [cart, saveCartProcess]);

  // Mark cart as abandoned when user closes/leaves the page with items
  // Use pagehide instead of beforeunload to allow bfcache restoration
  useEffect(() => {
    const handlePageHide = (e) => {
      // e.persisted = true means page is going into bfcache, don't mark as abandoned
      if (e.persisted) return;
      const current = cartRef.current;
      if (!current || current.length === 0) return;
      const resolvedUser = userRef.current || getStoredUser();
      const userId = (resolvedUser && resolvedUser._id) ? String(resolvedUser._id) : '';
      const payload = JSON.stringify({
        sessionId: sessionId.current,
        userId,
        items: current.map(i => ({ _id: i._id, name: i.name, price: i.price, qty: i.qty, image: (i.images && i.images[0]) || i.image || '', category: i.category, selectedColor: i.selectedColor || null })),
        subtotal: current.reduce((s, i) => s + i.price * i.qty, 0),
        status: 'abandoned',
      });
      const apiBase = (window._gwApiBase || (window.location.origin + '/api'));
      navigator.sendBeacon(apiBase + '/cart-process', new Blob([payload], { type: 'application/json' }));
    };
    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Called by Cart.js when user logs in/out to keep userRef current
  const setCartUser = useCallback((u) => {
    userRef.current = u || getStoredUser();
  }, []);

  // Stable function - never re-created, reads cart via ref
  const syncCart = useCallback(async () => {
    const current = cartRef.current;
    if (current.length === 0) return;
    try {
      const { data: products } = await api.get('/products?all=true');
      const notes = [];
      const updated = [];

      for (const item of current) {
        const live = products.find(p => String(p._id) === String(item._id));
        if (!live) continue; // deleted product - silently drop

        if (!live.inStock) {
          notes.push({ type: 'removed', name: item.name });
          continue; // out of stock - drop from cart
        }

        const livePrice = parseFloat(live.price);
        const cartPrice = parseFloat(item.price);
        if (livePrice !== cartPrice) {
          notes.push({ type: 'price', name: item.name, oldPrice: cartPrice, newPrice: livePrice });
          updated.push({ ...item, price: livePrice });
        } else {
          updated.push(item);
        }
      }

      // Always sync the cart (removes out-of-stock items even with no price notes)
      dispatch({ type: 'SYNC', cart: updated });
      if (notes.length > 0) setSyncNotes(notes);
    } catch {
      // silently fail - don't block checkout
    }
  }, []); // no deps - reads cart via ref

  const addToCart = (product, qty) => {
    var q = qty || 1;
    dispatch({ type: 'ADD', product: product, qty: q });
    setCartDrawerOpen(true);
    // Immediately record this add in item history
    var resolvedUser = userRef.current || getStoredUser();
    var userId = (resolvedUser && resolvedUser._id) ? String(resolvedUser._id) : '';
    var item = { _id: product._id, name: product.name, price: product.price, qty: q, image: (product.images && product.images[0]) || product.image || '', category: product.category, selectedColor: product.selectedColor || null, addedAt: new Date().toISOString() };
    api.post('/cart-process/add-history', {
      sessionId: sessionId.current,
      userId: userId,
      item: item,
    }).catch(function() {});
  };

  const addToCartSilent = (product, qty) => {
    var q = qty || 1;
    dispatch({ type: 'ADD', product: product, qty: q });
    // Record history silently too
    var resolvedUser = userRef.current || getStoredUser();
    var userId = (resolvedUser && resolvedUser._id) ? String(resolvedUser._id) : '';
    var item = { _id: product._id, name: product.name, price: product.price, qty: q, image: (product.images && product.images[0]) || product.image || '', category: product.category, selectedColor: product.selectedColor || null, addedAt: new Date().toISOString() };
    api.post('/cart-process/add-history', {
      sessionId: sessionId.current,
      userId: userId,
      item: item,
    }).catch(function() {});
  };

  const removeFromCart = (id) => {
    // Record removal in history before dispatching
    const item = cartRef.current.find(i => (i._id + (i.selectedColor?.name || '')) === id);
    if (item) {
      var resolvedUser = userRef.current || getStoredUser();
      var userId = (resolvedUser && resolvedUser._id) ? String(resolvedUser._id) : '';
      api.post('/cart-process/remove-history', {
        sessionId: sessionId.current,
        userId,
        item: { _id: item._id, name: item.name, selectedColor: item.selectedColor || null, removedAt: new Date().toISOString() },
      }).catch(function() {});
    }
    dispatch({ type: 'REMOVE', id });
  };

  const updateQty = (id, qty) =>
    dispatch({ type: 'UPDATE_QTY', id, qty });

  const clearCart = () => {
    // Record all current items as cleared
    if (cartRef.current.length > 0) {
      var resolvedUser = userRef.current || getStoredUser();
      var userId = (resolvedUser && resolvedUser._id) ? String(resolvedUser._id) : '';
      api.post('/cart-process/clear-history', {
        sessionId: sessionId.current,
        userId,
        clearedAt: new Date().toISOString(),
      }).catch(function() {});
    }
    dispatch({ type: 'CLEAR' });
  };

  const dismissSyncNotes = () => setSyncNotes([]);

  const itemCount = cart.reduce((sum, i) => sum + i.qty, 0);
  const subtotal  = cart.reduce((sum, i) => sum + i.price * i.qty, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, addToCartSilent, removeFromCart, updateQty, clearCart, syncCart, syncNotes, dismissSyncNotes, itemCount, subtotal, cartDrawerOpen, openCartDrawer, closeCartDrawer, saveCartProcess, setCartUser, sessionId: sessionId.current }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
